import type { Ref } from 'react';
import { Fragment } from 'react';

import { Typography } from '@remarkable/ark-web';
import { formatDistanceToNow } from 'date-fns';
import type { FleetUser } from 'src/api/endpoints/cloudEnterprise.types';
import { Button, Divider, Tooltip } from 'src/components';
import { DeviceIcon } from 'src/components/DeviceIcon';
import {
  DrawerClose,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from 'src/components/Drawer';
import { IntegrationIcon } from 'src/components/IntegrationIcon';
import { formatShortDate } from 'src/utils/formatDate';

import { Trans } from '@lingui/react/macro';
import { WarningTriangle, X } from '@remarkable/ark-icons';
import { getDeviceName } from '../utils/getDeviceName';
import { getFullName } from '../utils/getFullName';
import { getIntegrationName } from '../utils/getIntegrationName';
import { DeviceListItemDetailed } from './DeviceListItemDetailed';
import { getUserStatusLabel } from './fleetUserHelpers';
import { MemberActionMenu } from './MemberActionMenu';

export function MemberDrawer({
  data,
  closeMemberDrawer,
  ref,
}: {
  data?: FleetUser | null;
  closeMemberDrawer?: () => void;
  ref?: Ref<HTMLDivElement>;
}) {
  if (!data) {
    return (
      <div
        className="flex size-full min-w-0 flex-col justify-center gap-16 p-24"
        ref={ref}
      >
        <div className="flex w-full items-start justify-between gap-16">
          <DrawerHeader>
            <DrawerTitle asChild>
              <Typography variant="heading-xs" as="h2">
                No matching user
              </Typography>
            </DrawerTitle>
            <DrawerDescription>
              Please select a user in the table.
            </DrawerDescription>
          </DrawerHeader>
        </div>
      </div>
    );
  }

  const fullName = getFullName(data);
  const isMdmDeactivatedUser = !!data.mdmDisabled;
  const statusLabel = getUserStatusLabel(data);
  return (
    <div
      data-cy="member-drawer"
      className="flex size-full max-h-screen max-w-screen min-w-0"
      ref={ref}
    >
      <div className="max-h-full w-full overflow-y-scroll">
        <div className="sticky top-0 z-20 flex w-full items-start justify-between gap-16 bg-neutral-light-1 p-24">
          <DrawerHeader className="overflow-hidden">
            <DrawerTitle asChild>
              <Typography
                variant="heading-xs"
                as="h2"
                className="w-full truncate text-left"
              >
                {fullName || data.email}
              </Typography>
            </DrawerTitle>
            {!!fullName && (
              <DrawerDescription className="text-left">
                <span className="mr-8">{data.email}</span>
              </DrawerDescription>
            )}
          </DrawerHeader>
          <DrawerClose asChild>
            <Button
              variant="tertiary-neutral"
              size="medium"
              className="-mt-4 -mr-4"
            >
              <X />
            </Button>
          </DrawerClose>
        </div>

        <div className="flex flex-col gap-24 p-24">
          {data.issues?.length && (
            <ul className="flex flex-col gap-8">
              {data.issues.map((issue) =>
                issue.title ? (
                  <li
                    key={issue.title}
                    className="rounded-sm flex items-start gap-8 bg-neutral-light-2 p-4"
                  >
                    <WarningTriangle
                      size={24}
                      className="shrink-0 text-feedback-orange-500"
                    />
                    <Typography
                      variant="interface-sm"
                      className="mb-0 leading-140"
                    >
                      {issue.title}
                    </Typography>
                  </li>
                ) : null
              )}
            </ul>
          )}

          <div>
            <Typography variant="heading-xs" className="text-20">
              <Trans>Account</Trans>
            </Typography>

            <Divider />

            <div className="mt-16">
              <ul className="grid grid-cols-[1fr_1fr_auto]">
                <li>
                  <Typography
                    variant="interface-sm"
                    leadingNone
                    className="text-neutral-dark-1"
                  >
                    Role
                  </Typography>
                  <Typography variant="body-sm">
                    {data.roles?.map((r) => r.name).join(', ') || '-'}
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant="interface-sm"
                    leadingNone
                    className="text-neutral-dark-1"
                  >
                    Status
                  </Typography>
                  <Typography variant="body-sm">
                    {isMdmDeactivatedUser && (
                      <span
                        data-cy="mdm-deactivated-indicator"
                        className="mr-8 inline-block size-8 rounded-full bg-feedback-red-500 align-middle"
                      />
                    )}
                    {statusLabel}
                  </Typography>
                </li>
                <li>
                  <MemberActionMenu
                    member={data}
                    closeMemberDrawer={closeMemberDrawer}
                  />
                </li>
                {data.betaStatus?.enrolled && (
                  <li>
                    <Typography
                      variant="interface-sm"
                      leadingNone
                      className="truncate text-neutral-dark-1"
                    >
                      Beta software updates
                    </Typography>
                    <Typography variant="body-sm">
                      Enabled since{' '}
                      {formatShortDate(data.betaStatus.updatedAt, {
                        showYear: true,
                      })}{' '}
                      (version {data.betaStatus.version})
                    </Typography>
                  </li>
                )}
              </ul>
            </div>
          </div>

          <div>
            <Typography variant="heading-xs" className="text-20">
              Devices
            </Typography>

            <Divider />

            <ul className="mt-20 flex flex-col gap-8">
              {data.devices?.map((device, index) => {
                const isLastItem = index === (data.devices?.length ?? 0) - 1;
                return (
                  <Fragment key={device.serialNumber}>
                    <DeviceListItemDetailed
                      device={device}
                      userId={data.id}
                      userEmail={data.email}
                    />
                    {!isLastItem && <Divider className="my-0 opacity-50" />}
                  </Fragment>
                );
              })}
              {(data.devices?.length ?? 0) < 1 && (
                <li>
                  <Typography
                    variant="interface-sm"
                    leadingNone
                    className="text-neutral-dark-1"
                  >
                    No devices paired
                  </Typography>
                </li>
              )}
            </ul>
          </div>

          <div>
            <Typography variant="heading-xs" className="text-20">
              Apps
            </Typography>

            <Divider />

            <ul className="mt-20 flex flex-col gap-16">
              {data.companions?.map((companion, index) => (
                <Fragment key={companion.serialNumber}>
                  {index > 0 && (
                    <li>
                      <Divider />
                    </li>
                  )}
                  <li
                    className="flex w-full gap-16"
                    key={companion.serialNumber}
                  >
                    <div className="flex flex-1 items-center gap-16">
                      <DeviceIcon
                        device={companion}
                        size={48}
                        className="shrink-0"
                      />
                      <div>
                        <Typography variant="body-md" className="font-medium">
                          {getDeviceName(companion)}
                        </Typography>
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col">
                      <Typography
                        variant="interface-sm"
                        leadingNone
                        className="text-neutral-dark-1"
                      >
                        Last seen
                      </Typography>
                      <Typography variant="body-sm">
                        <Tooltip
                          title={formatShortDate(companion.lastSeen, {
                            showTime: true,
                            showYear: true,
                          })}
                        >
                          <span>
                            {formatDistanceToNow(
                              new Date(companion.lastSeen ?? ''),
                              { addSuffix: true }
                            )}
                          </span>
                        </Tooltip>
                      </Typography>
                    </div>
                  </li>
                </Fragment>
              ))}
              {(data.companions?.length ?? 0) < 1 && (
                <li>
                  <Typography
                    variant="interface-sm"
                    leadingNone
                    className="text-neutral-dark-1"
                  >
                    No apps paired
                  </Typography>
                </li>
              )}
            </ul>
          </div>

          <div>
            <Typography variant="heading-xs" className="text-20">
              Integrations
            </Typography>

            <Divider />

            <ul className="mt-20 flex flex-col gap-16">
              {data.integrations?.map((integration, index) => (
                <Fragment
                  key={
                    (integration.provider ?? '') +
                    (integration.createdAt ?? index.toString())
                  }
                >
                  {index > 0 && (
                    <li>
                      <Divider />
                    </li>
                  )}
                  <li className="flex w-full gap-16">
                    <div className="flex flex-1 items-center gap-16">
                      <IntegrationIcon
                        provider={integration.provider}
                        className="w-40 shrink-0 p-4"
                      />
                      <div>
                        <Typography variant="body-md" className="font-medium">
                          {getIntegrationName(integration)}
                          {integration.hasIssue && (
                            <Tooltip title="There is an issue with this integration. Please verify authentication on the integrations page.">
                              <WarningTriangle
                                size={24}
                                className="-mt-6 ml-6 inline-block text-feedback-orange-500"
                              />
                            </Tooltip>
                          )}
                        </Typography>
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col">
                      <Typography
                        variant="interface-sm"
                        leadingNone
                        className="text-neutral-dark-1"
                      >
                        Authenticated
                      </Typography>
                      <Typography variant="body-sm">
                        <Tooltip
                          title={formatShortDate(integration.createdAt, {
                            showTime: true,
                            showYear: true,
                          })}
                        >
                          <span>
                            {formatDistanceToNow(
                              new Date(integration.createdAt ?? ''),
                              { addSuffix: true }
                            )}
                          </span>
                        </Tooltip>
                      </Typography>
                    </div>
                  </li>
                </Fragment>
              ))}
              {(data.integrations?.length ?? 0) < 1 && (
                <li>
                  <Typography
                    variant="interface-sm"
                    leadingNone
                    className="text-neutral-dark-1"
                  >
                    No integrations added
                  </Typography>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
